/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Autocomplete,
  Chip,
} from "@mui/material";
import * as Yup from "yup";
import { useSelector } from "react-redux";

export type PromoCategory = "PROMO" | "EVALUATION" | "PAID";
export type PromoType =
  | "USER"
  | "USER_SPECIFIC"
  | "ORGANIZATION"
  | "ORG_SPECEFIC"
  | "ALL";

export interface PromoCodeCreate {
  code: string;
  category: PromoCategory;
  type: PromoType;
  title: string | null;
  description: string | null;
  user_promo_credits: number | null;
  org_promo_credits: number | null;
  max_usage_per_account: number | null;
  max_usage: number | null;
  status: "ACTIVE" | "INACTIVE";
  tnc: string | null;
  start_at: string;
  expire_at: string | null;
  valid_duration?: number | null;
  valid_for_users?: string[];
  valid_for_orgs?: string[];
}

interface CreatePromoCodeFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: PromoCodeCreate) => void;
}

const validationSchema = Yup.object().shape({
  code: Yup.string()
    .required("Promo code is required")
    .max(20, "Code too long"),
  title: Yup.string().max(255, "Title too long").nullable(),
  description: Yup.string().nullable(),
  category: Yup.mixed<PromoCategory>()
    .oneOf(["PROMO", "EVALUATION", "PAID"], "Invalid category")
    .required("Category is required"),
  type: Yup.mixed<PromoType>()
    .when("category", {
      is: "EVALUATION",
      then: (schema) =>
        schema
          .oneOf(
            ["ORGANIZATION", "ORG_SPECEFIC"],
            "Evaluation supports only Organization or Org Specific"
          )
          .required("Type is required"),
      otherwise: (schema) =>
        schema
          .oneOf(
            ["USER", "USER_SPECIFIC", "ORGANIZATION", "ORG_SPECEFIC", "ALL"],
            "Invalid type"
          )
          .required("Type is required"),
    })
    .required("Type is required"),
  user_promo_credits: Yup.number()
    .nullable()
    .when("type", {
      is: (val: PromoType) =>
        val === "USER" || val === "ALL" || val === "USER_SPECIFIC",
      then: (schema) =>
        schema.required("User credits required").min(0, "Must be at least 0"),
      otherwise: (schema) =>
        schema.test(
          "is-null",
          "User credits must be empty for this type",
          (value) => value === null
        ),
    }),
  org_promo_credits: Yup.number()
    .nullable()
    .when("type", {
      is: (val: PromoType) =>
        val === "ORGANIZATION" || val === "ALL" || val === "ORG_SPECEFIC",
      then: (schema) =>
        schema.required("Org credits required").min(0, "Must be at least 0"),
      otherwise: (schema) =>
        schema.test(
          "is-null",
          "Org credits must be empty for this type",
          (value) => value === null
        ),
    }),
  max_usage_per_account: Yup.number().nullable(),
  max_usage: Yup.number().nullable(),
  status: Yup.mixed<"ACTIVE" | "INACTIVE">()
    .oneOf(["ACTIVE", "INACTIVE"], "Invalid status")
    .required("Status is required"),
  tnc: Yup.string().max(500, "Too long").nullable(),
  start_at: Yup.date()
    .required("Start date required")
    .typeError("Invalid date"),
  expire_at: Yup.date()
    .nullable()
    .min(Yup.ref("start_at"), "Expire date must be after start date")
    .typeError("Invalid date"),
  valid_duration: Yup.number()
    .nullable()
    .when("category", {
      is: "EVALUATION",
      then: (schema) =>
        schema
          .required("Validity duration is required")
          .integer("Must be a whole number")
          .moreThan(0, "Must be greater than 0"),
      otherwise: (schema) => schema.nullable(),
    }),
  valid_for_users: Yup.array()
    .of(Yup.string().email("Invalid email"))
    .when("type", {
      is: (val: PromoType) => val === "USER_SPECIFIC",
      then: (schema) =>
        schema
          .min(1, "Add at least one user email")
          .required("User emails required"),
      otherwise: (schema) => schema.optional(),
    }),
  valid_for_orgs: Yup.array()
    .of(Yup.string().email("Invalid email"))
    .when(["type", "category"], {
      is: (type: PromoType, category: PromoCategory) =>
        type === "ORG_SPECEFIC" || category === "EVALUATION",
      then: (schema) =>
        schema
          .min(1, "Add at least one organization email")
          .required("Org emails required"),
      otherwise: (schema) => schema.optional(),
    }),
});

const CreatePromo: React.FC<CreatePromoCodeFormProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const initialState: PromoCodeCreate = {
    code: "",
    category: "PROMO",
    type: "USER",
    title: null,
    description: null,
    user_promo_credits: null,
    org_promo_credits: null,
    max_usage_per_account: null,
    max_usage: null,
    status: "ACTIVE",
    tnc: null,
    start_at: "",
    expire_at: null,
    valid_duration: 20,
    valid_for_users: [],
    valid_for_orgs: [],
  };

  const [values, setValues] = useState<PromoCodeCreate>(initialState);
  const [errors, setErrors] = useState<
    Partial<Record<keyof PromoCodeCreate, string>>
  >({});
  const CreatePromoCodeResponse = useSelector(
    (state: any) => state.CreatePromoCodeData
  );

  useEffect(() => {
    if (CreatePromoCodeResponse?.status && CreatePromoCodeResponse?.data) {
      setValues(initialState);
      onClose();
    }
  }, [CreatePromoCodeResponse]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> & {
      name?: string;
    }
  ) => {
    const { name, value } = e.target;
    if (!name) return;

    const numberFields: (keyof PromoCodeCreate)[] = [
      "user_promo_credits",
      "org_promo_credits",
      "max_usage_per_account",
      "max_usage",
      "valid_duration",
    ];

    setValues((prev) => ({
      ...prev,
      [name]:
        numberFields.includes(name as keyof PromoCodeCreate) && value === ""
          ? null
          : e.target.type === "number"
          ? Number(value)
          : value,
    }));
  };

  const handleCategoryChange = (category: PromoCategory) => {
    const allowedTypes: PromoType[] =
      category === "EVALUATION"
        ? ["ORGANIZATION", "ORG_SPECEFIC"]
        : ["USER", "USER_SPECIFIC", "ORGANIZATION", "ORG_SPECEFIC", "ALL"];

    setValues((prev) => ({
      ...prev,
      category,
      type: allowedTypes.includes(prev.type) ? prev.type : allowedTypes[0],
      valid_for_users:
        category === "EVALUATION" ? [] : prev.valid_for_users || [],
      valid_for_orgs: prev.valid_for_orgs || [],
    }));
  };

  const handleTypeChange = (type: PromoType) => {
    setValues((prev) => ({
      ...prev,
      type,
      valid_for_users:
        type === "USER_SPECIFIC" ? prev.valid_for_users || [] : [],
      valid_for_orgs:
        type === "ORG_SPECEFIC" || prev.category === "EVALUATION"
          ? prev.valid_for_orgs || []
          : [],
    }));
  };

  const showUserCredits =
    values.type === "USER" ||
    values.type === "ALL" ||
    values.type === "USER_SPECIFIC";
  const showOrgCredits =
    values.type === "ORGANIZATION" ||
    values.type === "ALL" ||
    values.type === "ORG_SPECEFIC";
  const showUserEmails = values.type === "USER_SPECIFIC";
  const showOrgEmails =
    values.type === "ORG_SPECEFIC" || values.category === "EVALUATION";

  const formatLabel = (option: string) =>
    option
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/^\w/, (c) => c.toUpperCase());

  const handleSubmit = async () => {
    try {
      const valid = await validationSchema.validate(values, {
        abortEarly: false,
      });
      setErrors({});

      const isUserType =
        valid.type === "USER" ||
        valid.type === "USER_SPECIFIC" ||
        valid.type === "ALL";
      const isOrgType =
        valid.type === "ORGANIZATION" ||
        valid.type === "ORG_SPECEFIC" ||
        valid.type === "ALL";

      const payload: PromoCodeCreate = {
        code: valid.code,
        category: valid.category,
        type: valid.type,
        title: valid.title?.trim() ? valid.title.trim() : null,
        description: valid.description?.trim() ? valid.description.trim() : null,
        user_promo_credits: isUserType
          ? valid.user_promo_credits ?? null
          : null,
        org_promo_credits: isOrgType ? valid.org_promo_credits ?? null : null,
        max_usage_per_account: valid.max_usage_per_account || null,
        max_usage: valid.max_usage || null,
        status: valid.status,
        tnc: valid.tnc ?? null,
        start_at:
          typeof valid.start_at === "string"
            ? valid.start_at
            : valid.start_at.toISOString(),
        expire_at: valid.expire_at
          ? typeof valid.expire_at === "string"
            ? valid.expire_at
            : valid.expire_at.toISOString()
          : null,
      };

      if (valid.category === "EVALUATION") {
        payload.valid_duration = valid.valid_duration ?? 20;
      }

      if (valid.valid_for_users?.length) {
        payload.valid_for_users = valid.valid_for_users.filter(
          (v): v is string => Boolean(v)
        );
      }
      if (valid.valid_for_orgs?.length) {
        payload.valid_for_orgs = valid.valid_for_orgs.filter((v): v is string =>
          Boolean(v)
        );
      }

      onSubmit(payload);
      setValues(initialState);
      onClose();
    } catch (err: any) {
      if (err.inner) {
        const formErrors: any = {};
        err.inner.forEach((validationError: any) => {
          formErrors[validationError.path as keyof PromoCodeCreate] =
            validationError.message;
        });
        setErrors(formErrors);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Promo Code</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            name="code"
            label="Promo Code"
            value={values.code}
            onChange={handleChange}
            error={Boolean(errors.code)}
            helperText={errors.code}
            fullWidth
          />
          <TextField
            name="title"
            label="Title (shown on billing)"
            value={values.title ?? ""}
            onChange={handleChange}
            error={Boolean(errors.title)}
            helperText={errors.title}
            fullWidth
          />
          <TextField
            name="description"
            label="Description (shown on billing)"
            value={values.description ?? ""}
            onChange={handleChange}
            error={Boolean(errors.description)}
            helperText={errors.description}
            multiline
            rows={2}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="category-label">Category</InputLabel>
            <Select
              labelId="category-label"
              name="category"
              value={values.category}
              onChange={(e) =>
                handleCategoryChange(e.target.value as PromoCategory)
              }
              error={Boolean(errors.category)}
              label="Category"
            >
              <MenuItem value="PROMO">Promo</MenuItem>
              <MenuItem value="EVALUATION">Evaluation</MenuItem>
              <MenuItem value="PAID">Paid</MenuItem>
            </Select>
            {errors.category && (
              <Box sx={{ color: "error.main" }}>{errors.category}</Box>
            )}
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="type-label">Type</InputLabel>
            <Select
              labelId="type-label"
              name="type"
              value={values.type}
              onChange={(e) => handleTypeChange(e.target.value as PromoType)}
              error={Boolean(errors.type)}
              label="Type"
            >
              {(values.category === "EVALUATION"
                ? ["ORGANIZATION", "ORG_SPECEFIC"]
                : [
                    "USER",
                    "USER_SPECIFIC",
                    "ORGANIZATION",
                    "ORG_SPECEFIC",
                    "ALL",
                  ]
              ).map((option) => (
                <MenuItem key={option} value={option}>
                  {formatLabel(option)}
                </MenuItem>
              ))}
            </Select>
            {errors.type && (
              <Box sx={{ color: "error.main" }}>{errors.type}</Box>
            )}
          </FormControl>
          {showUserCredits && (
            <TextField
              name="user_promo_credits"
              label="User Credits"
              type="number"
              inputProps={{ min: 0 }}
              value={values.user_promo_credits ?? ""}
              onChange={handleChange}
              error={Boolean(errors.user_promo_credits)}
              helperText={errors.user_promo_credits}
              fullWidth
            />
          )}
          {showOrgCredits && (
            <TextField
              name="org_promo_credits"
              label="Org Credits"
              type="number"
              inputProps={{ min: 0 }}
              value={values.org_promo_credits ?? ""}
              onChange={handleChange}
              error={Boolean(errors.org_promo_credits)}
              helperText={errors.org_promo_credits}
              fullWidth
            />
          )}
          {values.category === "EVALUATION" && (
            <TextField
              name="valid_duration"
              label="Validity Duration (days)"
              type="number"
              inputProps={{ min: 1 }}
              value={values.valid_duration ?? ""}
              onChange={handleChange}
              error={Boolean(errors.valid_duration)}
              helperText={errors.valid_duration}
              fullWidth
            />
          )}
          {showUserEmails && (
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={values.valid_for_users || []}
              onChange={(_, newValue) =>
                setValues((prev) => ({
                  ...prev,
                  valid_for_users: newValue
                    .map((item) => item.trim())
                    .filter(Boolean),
                }))
              }
              renderTags={(value: readonly string[], getTagProps) =>
                value.map((option: string, index: number) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                    key={option + index}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Valid for Users (emails)"
                  placeholder="Type email and press Enter"
                  error={Boolean(errors.valid_for_users)}
                  helperText={errors.valid_for_users}
                />
              )}
            />
          )}
          {showOrgEmails && (
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={values.valid_for_orgs || []}
              onChange={(_, newValue) =>
                setValues((prev) => ({
                  ...prev,
                  valid_for_orgs: newValue
                    .map((item) => item.trim())
                    .filter(Boolean),
                }))
              }
              renderTags={(value: readonly string[], getTagProps) =>
                value.map((option: string, index: number) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                    key={option + index}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Valid for Organizations (emails)"
                  placeholder="Type email and press Enter"
                  error={Boolean(errors.valid_for_orgs)}
                  helperText={errors.valid_for_orgs}
                />
              )}
            />
          )}
          <TextField
            name="max_usage_per_account"
            label="Max Usage/Account"
            type="number"
            inputProps={{ min: 1 }}
            value={values.max_usage_per_account ?? ""}
            onChange={handleChange}
            error={Boolean(errors.max_usage_per_account)}
            helperText={errors.max_usage_per_account}
            fullWidth
          />
          <TextField
            name="max_usage"
            label="Max Usage"
            type="number"
            inputProps={{ min: 0 }}
            value={values.max_usage ?? ""}
            onChange={handleChange}
            error={Boolean(errors.max_usage)}
            helperText={errors.max_usage}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              name="status"
              value={values.status}
              onChange={(e) =>
                setValues((prev) => ({
                  ...prev,
                  status: e.target.value as any,
                }))
              }
              error={Boolean(errors.status)}
              label="Status"
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </Select>
            {errors.status && (
              <Box sx={{ color: "error.main" }}>{errors.status}</Box>
            )}
          </FormControl>
          <TextField
            name="tnc"
            label="Terms & Conditions"
            value={values.tnc ?? ""}
            onChange={handleChange}
            error={Boolean(errors.tnc)}
            helperText={errors.tnc}
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            name="start_at"
            label="Start Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={values.start_at}
            onChange={handleChange}
            error={Boolean(errors.start_at)}
            helperText={errors.start_at}
            fullWidth
          />
          <TextField
            name="expire_at"
            label="Expire Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={values.expire_at ?? ""}
            onChange={handleChange}
            error={Boolean(errors.expire_at)}
            helperText={errors.expire_at}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Create Promo
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePromo;
